import { isNaiuTenant } from "./tenant";
import {
  naiuStageHasDeliveryDate,
  naiuStageHasDiagnosisDate,
  naiuStageHasExpectedClosing,
  naiuStageHasPrice,
  naiuStageHasPurchaseDate,
} from "./naiuStageTasks";

export const STAGE_ACCENT: Record<string, string> = {
  captured: "#8B9CB3",
  qualified: "#6B8CA8",
  diagnosis_booked: "#7EC8E3",
  diagnosis_done: "#3DB8E8",
  proposal: "#00AAFF",
  negotiation: "#0090D9",
  conversation: "#7EC8E3",
  cart: "#00AAFF",
  payment: "#0090D9",
  repurchase: "#2DD4A8",
  won: "#12B886",
  lost: "#E25555",
  onboarding: "#2DD4A8",
  active: "#00AAFF",
};

export const TEMPERATURE_COLOR: Record<string, string> = {
  cold: "#8B9CB3",
  warm: "#7EC8E3",
  hot: "#00AAFF",
  "in-contract": "#12B886",
};

export const CLOSED_DEAL_STAGES = new Set(["won", "lost"]);

/** Quoted value is captured from proposal onward. Earlier stages stay at 0. */
export const PRICED_DEAL_STAGES = new Set([
  "proposal",
  "negotiation",
  "won",
  "lost",
  "onboarding",
  "active",
]);

export function stageHasPrice(stage: string): boolean {
  if (isNaiuTenant()) {
    return naiuStageHasPrice(stage);
  }
  return PRICED_DEAL_STAGES.has(stage);
}

const DIAGNOSIS_DATE_STAGES = new Set([
  "diagnosis_booked",
  "diagnosis_done",
  "proposal",
  "negotiation",
  "won",
  "lost",
  "onboarding",
  "active",
]);

const PURCHASE_DATE_STAGES = new Set(["won", "onboarding", "active"]);
const DELIVERY_DATE_STAGES = new Set(["won", "onboarding", "active"]);
const EXPECTED_CLOSING_STAGES = new Set([
  "captured",
  "qualified",
  "diagnosis_booked",
  "diagnosis_done",
  "proposal",
  "negotiation",
]);

export function stageHasDiagnosisDate(stage: string): boolean {
  if (isNaiuTenant()) {
    return naiuStageHasDiagnosisDate(stage);
  }
  return DIAGNOSIS_DATE_STAGES.has(stage);
}

export function stageHasPurchaseDate(stage: string): boolean {
  if (isNaiuTenant()) {
    return naiuStageHasPurchaseDate(stage);
  }
  return PURCHASE_DATE_STAGES.has(stage);
}

export function stageHasDeliveryDate(stage: string): boolean {
  if (isNaiuTenant()) {
    return naiuStageHasDeliveryDate(stage);
  }
  return DELIVERY_DATE_STAGES.has(stage);
}

export function stageHasExpectedClosing(stage: string): boolean {
  if (isNaiuTenant()) {
    return naiuStageHasExpectedClosing(stage);
  }
  return EXPECTED_CLOSING_STAGES.has(stage);
}

export function isClosingGoalMet(
  expectedClosingDate: string | null | undefined,
  purchaseDate: string | null | undefined,
): boolean | null {
  if (!expectedClosingDate || !purchaseDate) {
    return null;
  }
  return purchaseDate.slice(0, 10) <= expectedClosingDate.slice(0, 10);
}

export function temperatureFromStage(stage: string): string {
  if (stage === "won" || stage === "onboarding" || stage === "active") {
    return "in-contract";
  }
  if (stage === "proposal" || stage === "negotiation") {
    return "hot";
  }
  if (stage.startsWith("diagnosis")) {
    return "warm";
  }
  return "cold";
}

export function isLightHex(hex: string): boolean {
  const raw = hex.replace("#", "");
  if (raw.length < 6) {
    return false;
  }
  const red = Number.parseInt(raw.slice(0, 2), 16);
  const green = Number.parseInt(raw.slice(2, 4), 16);
  const blue = Number.parseInt(raw.slice(4, 6), 16);
  return (red * 299 + green * 587 + blue * 114) / 1000 > 155;
}

export function getStageAccent(stage: string): string {
  return STAGE_ACCENT[stage] ?? "#8B9CB3";
}

export function formatMoney(amount: number, currency = "COP"): string {
  const isCop = currency === "COP";
  return new Intl.NumberFormat(isCop ? "es-CO" : "en-US", {
    style: "currency",
    currency: isCop ? "COP" : currency,
    maximumFractionDigits: isCop ? 0 : 2,
  }).format(amount);
}

export function formatCop(amount: number): string {
  return formatMoney(amount, "COP");
}
